import { z } from "zod";
import {
  createDateFilter,
  createDateRangeFilter,
  createRelativeDateFilter,
  createStringInFilter,
  FilterSpec,
  StringInFilterSpec,
} from "@renewmap/filter";
import { defaultUpdateTypes } from "./changelog";
import { visTypeSchema } from "./project";

export const notificationFrequencySchema = z.enum([
  "immediately",
  "daily",
  "weekly",
]);

export type NotificationFrequency = z.infer<typeof notificationFrequencySchema>;

// FilterSpec expects Date objects, dates returned as strings from the network/DB
const deserialiseSpec = (spec: FilterSpec) => {
  if (spec.type === "date" && spec.value !== null) {
    return spec.operator === "range"
      ? createDateRangeFilter([
          new Date(spec.value[0]),
          new Date(spec.value[1]),
        ])
      : createDateFilter(spec.operator, new Date(spec.value));
  }
  return spec;
};

export const deserialiseFilters = (filters: FieldFilterSpec): FieldFilterSpec =>
  Object.fromEntries(
    Object.entries(filters).map(([field_name, filter]) => [
      field_name,
      deserialiseSpec(filter),
    ]),
  );

export const defaultChangelogFilters: FieldFilterSpec = {
  timestamp_cl: createRelativeDateFilter("after", "last2Weeks"),
  update_type_cl: createStringInFilter(defaultUpdateTypes),
};

const defaultWatchlist = {};

export const defaultNetworkFilters = {
  infrastructure_status_net: createStringInFilter([
    "Development",
    "Construction",
    "Operating",
  ]),
};

export const watchlistSchema = z
  .object({
    id: z.custom<StringInFilterSpec>((v) => v && typeof v === "object"),
  })
  .partial();

export const defaultUserPreferences: UserPreferences = {
  changelog_read_ids: [],
};

export const filterPreferenceSchema = z.record(
  z.string(),
  z.custom<FilterSpec>((v) => v && typeof v === "object"),
);

export const userPreferencesSchema = z.object({
  changelog_read_ids: z.array(z.string()).default([]),
});

export const partialUserPreferencesSchema = userPreferencesSchema.partial();

export type UserPreferences = z.infer<typeof userPreferencesSchema>;

export type PartialUserPreferences = z.infer<
  typeof partialUserPreferencesSchema
>;

export type FieldFilterSpec = z.infer<typeof filterPreferenceSchema>;

export type WatchlistFilterSpec = z.infer<typeof watchlistSchema>;

// User scope preferences: per user settings across containers
export const userScopedPreferencesSchema = z.object({
  changelog_read_ids: z.array(z.string()).default([]),
});
export type UserScopedPreferences = z.infer<typeof userScopedPreferencesSchema>;
export const partialUserScopedPreferencesSchema =
  userScopedPreferencesSchema.partial();
export type PartialUserScopedPreferences = z.infer<
  typeof partialUserScopedPreferencesSchema
>;

// Visibility of the boolean layer toggles, keyed by filter group then layer.
export const visibleLayersSchema = z.record(
  z.string(),
  z.record(z.string(), z.boolean()),
);
export type VisibleLayers = z.infer<typeof visibleLayersSchema>;

// Container scope preferences: per container settings across users
export const containerScopedPreferenceSchema = z.object({
  changelog_filter_v2: filterPreferenceSchema
    .default(defaultChangelogFilters)
    .transform(deserialiseFilters),
  watchlist_filter: watchlistSchema.default(defaultWatchlist),
  base_map_layer: z.string().default("satellite"),
  project_visualisation_v2: visTypeSchema.default("boundaries_prj"),
  network_visualisation_v2: visTypeSchema.default("voltage_net"),
  visible_layers: visibleLayersSchema.default({}),
  project_entity_filter_v2: filterPreferenceSchema
    .default({})
    .transform(deserialiseFilters),
  network_entity_filter_v2: filterPreferenceSchema
    .default(defaultNetworkFilters)
    .transform(deserialiseFilters),
  org_entity_filter_v2: filterPreferenceSchema
    .default({})
    .transform(deserialiseFilters),
  grid_state_prj: z.unknown().default(undefined),
  grid_state_net: z.unknown().default(undefined),
  grid_state_org: z.unknown().default(undefined),
});
export type ContainerScopedPreferences = z.infer<
  typeof containerScopedPreferenceSchema
>;
export const partialContainerScopedPreferencesSchema =
  containerScopedPreferenceSchema.partial();
export type PartialContainerScopedPreferences = z.infer<
  typeof partialContainerScopedPreferencesSchema
>;

const granularitySchema = z.enum(["hh", "d", "w", "m", "y"]);

const timeWindowSchema = z.object({ from: z.number(), to: z.number() });

export const operationalChartControlsSchema = z.object({
  mode: z.enum(["24h", "range"]).default("24h"),
  granularity: granularitySchema.nullable().default(null),
});
export type OperationalChartControls = z.infer<
  typeof operationalChartControlsSchema
>;

export const defaultOperationalChartControls: OperationalChartControls = {
  mode: "24h",
  granularity: null,
};

export const timePickerValueSchema = z.object({
  outer: timeWindowSchema,
  inner: timeWindowSchema,
  granularity: granularitySchema,
  hours: z.array(z.number().int().min(0).max(23)).max(24).catch([]).default([]),
});
export type TimePickerValue = z.infer<typeof timePickerValueSchema>;

// User-container scope preferences: per user settings per container
export const userContainerScopedPreferencesSchema = z.object({
  watchlist_notify: z.boolean().default(true),
  changelog_notify: z.boolean().default(false),
  notify_frequency: notificationFrequencySchema.default("immediately"),
  operational_chart_controls: operationalChartControlsSchema.default(
    defaultOperationalChartControls,
  ),
  time_picker_value: timePickerValueSchema.nullable().default(null),
});
export type UserContainerScopedPreferences = z.infer<
  typeof userContainerScopedPreferencesSchema
>;
export const partialUserContainerScopedPreferencesSchema =
  userContainerScopedPreferencesSchema.partial();
export type PartialUserContainerScopedPreferences = z.infer<
  typeof partialUserContainerScopedPreferencesSchema
>;

export const containerPreferencesSchema = containerScopedPreferenceSchema.merge(
  userContainerScopedPreferencesSchema,
);
export type ContainerPreferences = z.infer<typeof containerPreferencesSchema>;
export const partialContainerPreferencesSchema =
  containerPreferencesSchema.partial();
export type PartialContainerPreferences = z.infer<
  typeof partialContainerPreferencesSchema
>;
